import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Typography,
  Select,
  DatePicker,
  Tabs,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, CheckCircleOutlined, BookOutlined, ProjectOutlined } from '@ant-design/icons';
import {
  useRiskAssessments,
  useApproveRiskAssessment,
  useCreateRiskAssessment,
  useEnsureCompanyRegister,
} from '../../hooks/useRisk';
import { RiskAssessment } from '../../services/risk.service';
import { useProjects } from '../../hooks/useProjects';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { TextArea } = Input;

const RiskAssessmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'Company' | 'Project'>('Company');
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<RiskAssessment | null>(null);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');

  const { data: assessments = [], isLoading, refetch } = useRiskAssessments({ scope: tab });
  const { data: projects = [], isLoading: projectsLoading } = useProjects({ is_active: true });
  const approveMutation = useApproveRiskAssessment();
  const createMutation = useCreateRiskAssessment();
  const ensureRegister = useEnsureCompanyRegister();

  const handleApprove = (assessment: RiskAssessment) => {
    setSelectedAssessment(assessment);
    form.resetFields();
    setIsApprovalModalVisible(true);
  };

  const handleApproveSubmit = async (values: any) => {
    await approveMutation.mutateAsync({ id: selectedAssessment!.id, notes: values.notes });
    setIsApprovalModalVisible(false);
    form.resetFields();
    refetch();
  };

  const handleOpenCreateProject = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      assessment_date: dayjs(),
      overall_risk_level: 'Acceptable',
      scope: 'Project',
    });
    setIsCreateModalVisible(true);
  };

  const handleCreateSubmit = async (values: any) => {
    const created = await createMutation.mutateAsync({
      scope: 'Project',
      project_id: values.project_id,
      assessment_date: values.assessment_date
        ? values.assessment_date.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD'),
      overall_risk_level: values.overall_risk_level,
      status: 'Draft',
    });
    setIsCreateModalVisible(false);
    createForm.resetFields();
    navigate(`/grc/risk-assessments/${created.id}`);
  };

  const handleOpenCompanyRegister = async () => {
    try {
      const register = await ensureRegister.mutateAsync();
      if (!register?.id) {
        throw new Error('Company register was created but no id was returned');
      }
      navigate(`/grc/risk-assessments/${register.id}`);
    } catch {
      // error toast handled by mutation onError
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Draft: 'default',
      'Under Review': 'processing',
      Approved: 'success',
      Active: 'success',
      Superseded: 'warning',
    };
    return colors[status] || 'default';
  };

  const getRiskLevelColor = (level: string) => {
    const colors: Record<string, string> = {
      Acceptable: 'green',
      Moderate: 'orange',
      High: 'red',
      Low: 'green',
      Medium: 'orange',
      Critical: 'red',
    };
    return colors[level] || 'default';
  };

  const filteredAssessments = assessments.filter((assessment) =>
    assessment.assessment_number.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns: ColumnsType<RiskAssessment> = [
    {
      title: 'Assessment #',
      dataIndex: 'assessment_number',
      key: 'assessment_number',
      width: 140,
      render: (val, record) => (
        <Button type="link" style={{ paddingInline: 0 }} onClick={() => navigate(`/grc/risk-assessments/${record.id}`)}>
          {val}
        </Button>
      ),
    },
    ...(tab === 'Project'
      ? [
          {
            title: 'Project',
            key: 'project',
            render: (_: any, record: RiskAssessment) => record.project?.project_name || 'N/A',
          } as ColumnsType<RiskAssessment>[number],
        ]
      : []),
    {
      title: 'Risk Level',
      dataIndex: 'overall_risk_level',
      key: 'overall_risk_level',
      width: 120,
      render: (level: string) => <Tag color={getRiskLevelColor(level)}>{level}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Assessment Date',
      dataIndex: 'assessment_date',
      key: 'assessment_date',
      width: 130,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => navigate(`/grc/risk-assessments/${record.id}`)}>
            Open
          </Button>
          {!['Approved', 'Active', 'Superseded'].includes(record.status) && (
            <Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => handleApprove(record)}>
              Approve
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Risk Assessment & Contingency Register
            </Title>
            <p style={{ margin: '4px 0 0', color: '#666' }}>
              API Spec Q1 — NTES-QSF-QA-QC-08 / NTES-QSP-QA-QC-006
            </p>
          </div>
          <Space>
            {tab === 'Company' ? (
              <Button
                type="primary"
                icon={<BookOutlined />}
                loading={ensureRegister.isPending}
                onClick={handleOpenCompanyRegister}
              >
                Open Company Register
              </Button>
            ) : (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateProject}>
                New Project Assessment
              </Button>
            )}
          </Space>
        </div>

        <Alert
          type="info"
          showIcon
          message="Company register is the living QMS risk & contingency record. Project assessments are snapshots for a specific project."
        />

        <Card>
          <Tabs
            activeKey={tab}
            onChange={(k) => setTab(k as 'Company' | 'Project')}
            items={[
              { key: 'Company', label: 'Company Register', icon: <BookOutlined /> },
              { key: 'Project', label: 'Project Assessments', icon: <ProjectOutlined /> },
            ]}
          />
          <Space style={{ marginBottom: 16 }}>
            <Search
              placeholder="Search assessment number"
              allowClear
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 280 }}
            />
          </Space>
          <Table
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={filteredAssessments}
            size="small"
            pagination={{ pageSize: 20, showTotal: (t) => `Total ${t}` }}
            scroll={{ x: 900 }}
          />
        </Card>
      </Space>

      <Modal
        title="Approve Risk Assessment"
        open={isApprovalModalVisible}
        onCancel={() => setIsApprovalModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleApproveSubmit}>
          <Form.Item name="notes" label="Approval Notes">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={approveMutation.isPending}>
                Approve
              </Button>
              <Button onClick={() => setIsApprovalModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New Project Risk Assessment"
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateSubmit}>
          <Form.Item name="project_id" label="Project" rules={[{ required: true, message: 'Select a project' }]}>
            <Select
              showSearch
              loading={projectsLoading}
              optionFilterProp="label"
              options={projects.map((p: any) => ({
                value: p.id,
                label: p.project_name || p.project_code || p.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="assessment_date" label="Assessment Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="overall_risk_level" label="Overall Risk Level" initialValue="Acceptable">
            <Select
              options={[
                { value: 'Acceptable', label: 'Acceptable' },
                { value: 'Moderate', label: 'Moderate' },
                { value: 'High', label: 'High' },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
                Create
              </Button>
              <Button onClick={() => setIsCreateModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RiskAssessmentsPage;
